import { AppImages } from "app/appImages";
import { GenericAbortSignal } from "axios";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { IAutoCompleteProps } from "components/MprcAutoComplete/AutoComplete";
import { UserSelectionEntityType, UserSelectionEntry, UserSuggestionsType } from "generated/CoreApi_v1";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useEffect, useState } from "react";

////////////////////////////////////////////////////////////////////////////

export type UserAutoCompleteQueryParams = {
    pageNumber: number;
    pageSize: number;
    searchText?: string | null;
};

interface IProps {
    type?: UserSuggestionsType;
    includeYourself?: boolean;
    selectionHistoryType?: UserSelectionEntityType;
    usingPermissions?: string[] | null;
    hasPermissions?: string[] | null;
    onGetDataCustom?: (data: UserAutoCompleteQueryParams, signal: GenericAbortSignal | undefined) => Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>;
}

type Props = IProps & Omit<IAutoCompleteProps, 'onGetSuggestions' | 'emptyThumbnail'>;

////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete de usuários
 * @param props
 */
export const UserAutoComplete: React.FC<Props> = (props) => {

    const api = useCoreApi();
    const session = useSession();
    const { type = UserSuggestionsType.Any, usingPermissions, hasPermissions, includeYourself, onError, onGetDataCustom, selectionHistoryType } = props;

    const [saveSelection, setSaveSelection] = useState<AutoCompleteDataModel[] | undefined>(undefined);
    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number, trySelectedHistory: boolean, signal: GenericAbortSignal | undefined): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();

        if (onGetDataCustom != undefined) {
            return onGetDataCustom({ pageNumber, pageSize, searchText }, signal);
        } else {
            return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
                api.v1.suggestions.users({
                    searchText: searchText,
                    type: type ?? UserSuggestionsType.Any,
                    selectionHistoryType: trySelectedHistory ? selectionHistoryType ?? null : null,
                    includeYourself: includeYourself ?? false,
                    usingPermissions: usingPermissions,
                    hasPermissions: hasPermissions,
                    pageNumber: pageNumber,
                    pageSize: pageSize,
                }, { signal })
                    .then(result => {
                        resolve({
                            items: result.data.items as AutoCompleteDataModel[],
                            totalRows: result.data.totalRows
                        });
                    })
                    .catch(error => {
                        if (!isCanceledError(error)) {
                            resolve({ items: [], totalRows: 0 });
                            if (onError) onError(error);
                        }
                    });
            });
        }
    };

    const handleChange = (items: AutoCompleteDataModel[]) => {
        setSaveSelection(items);
        props.onChange(items);
    };

    /// Salva itens selecionados.
    useEffect(() => {
        if (saveSelection != undefined && saveSelection.length > 0 && selectionHistoryType != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.map((v) => ({
                    entityType: selectionHistoryType,
                    entityId: v.key,
                    otherInfo: 'false'
                } as UserSelectionEntry))
            };

            api.v1.user.saveSelectionHistory(payload)
                .then(() => {
                    setSaveSelection(undefined);
                })
                .catch((e) => {
                    setSaveSelection(undefined);
                });
        }
    }, [saveSelection]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcAutoComplete
            {...props}
            onChange={handleChange}
            emptyThumbnail={AppImages.UserThumbnail}
            onGetSuggestions={getSuggestions}
        />
    );
};
